import { FC } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import { FieldInput } from 'src/components';
import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { NPIPhoneInput } from 'src/components/FormikComponents';
import Loader from 'src/components/Loader/Loader';
import { SearchInput } from 'src/components/SearchInput';
import { useValidateEmail } from 'src/hooks';
import { isIreland } from 'src/lib/countryConfig';
import { supportedCountries } from 'src/utils/utils';

import { Card } from '../card';

interface ContactInformationComponent {
    heading: string;
    formik: FormikProps<unknown>;
    onFindAddress: (value: string, formValue: string) => Promise<void>;
    postalLabel: string;
    countryCode?: string;
    namePrefix?: string;
    formattedAddress?: string;
}

const ContactInformationComponent: FC<ContactInformationComponent> = ({
    heading,
    formik,
    onFindAddress,
    postalLabel,
    countryCode,
    namePrefix,
    formattedAddress,
}) => {
    const { onValidateEmail, validatingEmail } = useValidateEmail();

    return (
        <Card heading={heading}>
            {validatingEmail && <Loader />}
            <Box display="flex" justifyContent="space-between" marginY="24px">
                <FieldInput
                    name={`${namePrefix}firstName`}
                    label="First Name"
                    boxStyling={{ maxWidth: '50%' }}
                />
                <FieldInput name={`${namePrefix}lastName`} label="Last Name" />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <SearchInput
                    name={`${namePrefix}email`}
                    label="Email Address"
                    value={formik.values[`${namePrefix}email`]}
                    onChange={(e) => {
                        formik.handleChange(e);
                    }}
                    error={formik.errors.email}
                    boxStyling={{ maxWidth: '50%' }}
                    searchText="Validate"
                    onSearch={async (email) => {
                        if (email) {
                            const trimmedEmail = email.trim();

                            await onValidateEmail(
                                trimmedEmail,
                                () => {
                                    formik.setFieldError(
                                        `${namePrefix}email`,
                                        'Please enter a valid email'
                                    );
                                },
                                () => {
                                    formik.setFieldError(`${namePrefix}email`, null);
                                }
                            );
                        }
                    }}
                />

                <NPIPhoneInput
                    fieldName={`${namePrefix}telephoneNumber`}
                    label="Telephone Number"
                    placeholder="Enter Telephone Number"
                    defaultCountry={countryCode}
                    dataTestId={`telephone-number-${namePrefix}`}
                />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <NPIPhoneInput
                    fieldName={`${namePrefix}mobilePhoneNumber`}
                    label="Mobile Number"
                    placeholder="Enter Mobile Number"
                    defaultCountry={countryCode}
                    dataTestId={`mobile-number-${namePrefix}`}
                />
                <FieldInput name="bci_position" label="Position" boxStyling={{ maxWidth: '50%' }} />
            </Box>
            <Typography sx={{ fontSize: '20px', color: '#434341' }}>Registered Address</Typography>
            <Box height="2px" width="100%" margin="0px auto 24px" bgcolor="#F39E4E" />

            {/* TODO: (KM) Fix this to use search */}
            <SearchInput
                name={`${namePrefix}ra_eircode`}
                label={postalLabel}
                value={formik.values[`${namePrefix}ra_eircode`]}
                onChange={(e) => {
                    formik.handleChange(e);
                }}
                error={formik.errors[`${namePrefix}ra_eircode`]}
                boxStyling={{ maxWidth: '50%' }}
                onSearch={(value) => {
                    return onFindAddress(value, `${namePrefix}ra`);
                }}
            />
            <Box display="flex" justifyContent="space-between" marginY="24px">
                <FieldInput
                    name={`${namePrefix}ra_addressLine1`}
                    label="Address Line 1"
                    boxStyling={{ maxWidth: '50%' }}
                />
                <FieldInput name={`${namePrefix}ra_addressLine2`} label="Address Line 2" />
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FieldInput name={`${namePrefix}ra_city`} label="Town/City" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CountrySelectInput
                            label="Country"
                            fieldName={`${namePrefix}ra_country`}
                            setFieldValue={formik.setFieldValue}
                            filterByAlpha2Codes={supportedCountries(isIreland)}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box display="flex" justifyContent="space-between" marginBottom="24px">
                <FieldInput
                    name={`${namePrefix}ra_eircode`}
                    label={postalLabel}
                    boxStyling={{ maxWidth: '50%' }}
                />
            </Box>
        </Card>
    );
};

export default ContactInformationComponent;
